import React, { useState, useEffect } from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Input } from "../components/duvi/Input";
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link,
  Image,
  Code,
  Minus,
  X,
} from "lucide-react";
import { useParams, useNavigate } from "react-router";
import ReactMarkdown from "react-markdown";
import { kb, type KBArticle } from "../api/client";
import { useQuery } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

export function KnowledgeBaseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [content, setContent] = useState("");
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: existingArticle, isLoading: isLoadingArticle } = useQuery(
    () => kb.get(id!),
    [id],
    { enabled: !!id },
  );

  // Populate form when editing an existing article
  useEffect(() => {
    if (existingArticle) {
      setTitle(existingArticle.title);
      setCategory(existingArticle.category);
      setTags(existingArticle.tags);
      setContent(existingArticle.content);
    }
  }, [existingArticle]);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      if (id) {
        await kb.update(id, { title, content, category, tags });
      } else {
        await kb.create({ title, content, category, tags });
      }
      navigate("/knowledge-base");
    } catch {
      // error handling deferred
    } finally {
      setIsPublishing(false);
    }
  };

  const categories = ["Technical", "Business", "Support"];

  const insertMarkdown = (before: string, after: string = "") => {
    const textarea = document.getElementById(
      "markdown-editor",
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const newText =
      content.substring(0, start) +
      before +
      selectedText +
      after +
      content.substring(end);

    setContent(newText);

    // Set cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length,
      );
    }, 0);
  };

  const toolbarButtons = [
    { icon: Bold, label: "Bold", action: () => insertMarkdown("**", "**") },
    { icon: Italic, label: "Italic", action: () => insertMarkdown("_", "_") },
    {
      icon: Heading1,
      label: "Heading 1",
      action: () => insertMarkdown("\n# ", "\n"),
    },
    {
      icon: Heading2,
      label: "Heading 2",
      action: () => insertMarkdown("\n## ", "\n"),
    },
    {
      icon: Heading3,
      label: "Heading 3",
      action: () => insertMarkdown("\n### ", "\n"),
    },
    {
      icon: List,
      label: "Unordered list",
      action: () => insertMarkdown("\n- ", "\n"),
    },
    {
      icon: ListOrdered,
      label: "Ordered list",
      action: () => insertMarkdown("\n1. ", "\n"),
    },
    { icon: Link, label: "Link", action: () => insertMarkdown("[", "](url)") },
    {
      icon: Image,
      label: "Image",
      action: () => insertMarkdown("![alt text](", ")"),
    },
    {
      icon: Code,
      label: "Code block",
      action: () => insertMarkdown("\n```\n", "\n```\n"),
    },
    {
      icon: Minus,
      label: "Divider",
      action: () => insertMarkdown("\n---\n", ""),
    },
  ];

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <AppShell
      breadcrumbs={["Knowledge Base", title || "New Article"]}
      activeMenuItem="Knowledge Base"
      notificationCount={3}
    >
      <div data-testid={testIds.kbEditorPage}>
        {/* Top Bar Actions */}
        <div className="flex items-center justify-end gap-4 mb-6 pb-4 border-b border-duvi-border">
          <span
            className="text-duvi-text-secondary"
            style={{ fontSize: "var(--text-xs)" }}
          >
            Last saved
          </span>
          <Button data-testid={testIds.kbEditorSaveDraft} variant="secondary">
            Save draft
          </Button>
          <Button
            data-testid={testIds.kbEditorPublish}
            variant="primary"
            onClick={handlePublish}
            disabled={isPublishing || !title || !category || !content}
          >
            {isPublishing ? "Publishing..." : "Publish"}
          </Button>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-[60%_40%] gap-6">
          {/* Left Column - Editor */}
          <div className="space-y-4">
            {/* Title Input */}
            <input
              data-testid={testIds.kbEditorTitle}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Article title"
              className="w-full bg-transparent border-none outline-none text-duvi-text-primary placeholder:text-duvi-text-muted"
              style={{
                fontSize: "var(--text-xl)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            />

            {/* Category and Tags */}
            <div className="flex items-center gap-3">
              <select
                data-testid={testIds.kbEditorCategory}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="px-3 py-2 bg-duvi-surface border border-duvi-border rounded-[6px] text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors"
                style={{ fontSize: "var(--text-sm)" }}
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              <div className="flex-1 px-3 py-2 bg-duvi-surface border border-duvi-border rounded-[6px] flex flex-wrap gap-2 items-center">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-duvi-elevated rounded text-duvi-text-primary"
                    style={{ fontSize: "var(--text-xs)" }}
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="hover:text-duvi-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  data-testid={testIds.kbEditorTagInput}
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  placeholder={
                    tags.length === 0 ? "Add tags (press Enter)..." : ""
                  }
                  className="flex-1 min-w-[150px] bg-transparent outline-none text-duvi-text-primary placeholder:text-duvi-text-muted"
                  style={{ fontSize: "var(--text-sm)" }}
                />
              </div>
            </div>

            {/* Markdown Toolbar */}
            <Card className="p-3">
              <div className="flex items-center gap-1 flex-wrap">
                {toolbarButtons.map((button, index) => (
                  <div key={index} className="relative group">
                    <button
                      onClick={button.action}
                      className="p-2 hover:bg-duvi-elevated rounded transition-colors text-duvi-text-secondary hover:text-duvi-text-primary"
                      title={button.label}
                    >
                      <button.icon className="w-4 h-4" />
                    </button>
                    {/* Tooltip */}
                    <div
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-duvi-elevated border border-duvi-border rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10"
                      style={{ fontSize: "var(--text-2xs)" }}
                    >
                      {button.label}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Markdown Editor */}
            <Card className="p-0 overflow-hidden">
              <div className="flex">
                {/* Line Numbers */}
                <div
                  className="bg-duvi-bg px-3 py-4 text-right select-none"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontFamily: "monospace",
                  }}
                >
                  {content.split("\n").map((_, index) => (
                    <div
                      key={index}
                      className="text-duvi-text-secondary leading-6"
                    >
                      {index + 1}
                    </div>
                  ))}
                </div>

                {/* Editor */}
                <textarea
                  id="markdown-editor"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Start writing your article in Markdown..."
                  className="flex-1 p-4 bg-transparent border-none outline-none text-duvi-text-primary placeholder:text-duvi-text-muted resize-none font-mono leading-6"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontFamily: "monospace",
                    minHeight: "400px",
                  }}
                />
              </div>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-4">
            <div className="sticky top-6">
              <div
                className="text-duvi-text-secondary mb-3"
                style={{ fontSize: "var(--text-xs)" }}
              >
                Preview
              </div>
              <Card className="p-6">
                <div className="prose prose-invert prose-sm max-w-none">
                  {title && (
                    <h1
                      className="text-duvi-text-primary mb-4"
                      style={{
                        fontSize: "var(--text-xl)",
                        fontWeight: "var(--font-weight-bold)",
                      }}
                    >
                      {title}
                    </h1>
                  )}

                  {content ? (
                    <div className="markdown-preview">
                      <ReactMarkdown
                        components={{
                          h1: ({ node, ...props }) => (
                            <h1
                              className="text-duvi-text-primary mb-4"
                              style={{
                                fontSize: "var(--text-lg)",
                                fontWeight: "var(--font-weight-bold)",
                              }}
                              {...props}
                            />
                          ),
                          h2: ({ node, ...props }) => (
                            <h2
                              className="text-duvi-text-primary mb-3 mt-6"
                              style={{
                                fontSize: "var(--text-md)",
                                fontWeight: "var(--font-weight-bold)",
                              }}
                              {...props}
                            />
                          ),
                          h3: ({ node, ...props }) => (
                            <h3
                              className="text-duvi-text-primary mb-2 mt-4"
                              style={{
                                fontSize: "var(--text-sm)",
                                fontWeight: "var(--font-weight-bold)",
                              }}
                              {...props}
                            />
                          ),
                          p: ({ node, ...props }) => (
                            <p
                              className="text-duvi-text-secondary mb-4"
                              style={{ fontSize: "var(--text-sm)" }}
                              {...props}
                            />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul
                              className="list-disc list-inside mb-4 text-duvi-text-secondary space-y-1"
                              style={{ fontSize: "var(--text-sm)" }}
                              {...props}
                            />
                          ),
                          ol: ({ node, ...props }) => (
                            <ol
                              className="list-decimal list-inside mb-4 text-duvi-text-secondary space-y-1"
                              style={{ fontSize: "var(--text-sm)" }}
                              {...props}
                            />
                          ),
                          code: ({ node, inline, ...props }: any) =>
                            inline ? (
                              <code
                                className="px-1.5 py-0.5 bg-duvi-elevated rounded text-duvi-accent font-mono"
                                style={{ fontSize: "var(--text-xs)" }}
                                {...props}
                              />
                            ) : (
                              <code
                                className="block p-3 bg-duvi-elevated rounded font-mono overflow-x-auto mb-4"
                                style={{ fontSize: "var(--text-xs)" }}
                                {...props}
                              />
                            ),
                          a: ({ node, ...props }) => (
                            <a
                              className="text-duvi-accent hover:underline"
                              {...props}
                            />
                          ),
                          hr: ({ node, ...props }) => (
                            <hr
                              className="border-duvi-border my-6"
                              {...props}
                            />
                          ),
                        }}
                      >
                        {content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p
                      className="text-duvi-text-secondary italic"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      Your article preview will appear here...
                    </p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
